import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { FileText } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { adminAction } from '@/lib/adminApi';
import {
  AdminPageHeader,
  AdminPageSkeleton,
  AdminSegmented,
  AdminStatRibbon,
  AdminSection,
  AdminPanel,
  AdminTable,
  AdminEmptyState,
  adminTableHeadCellClass,
  adminTableRowClass,
  adminTableCellClass,
} from '@/components/admin/AdminUI';

interface BlogAnalytics {
  total_views: number;
  posts_viewed: number;
  avg_views_per_day: number;
  views_by_day: { date: string; views: number }[];
  top_posts: { slug: string; post_id: string | null; title: string; views: number }[];
}

const RANGE_OPTS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

export default function AdminBlogAnalytics() {
  const [days, setDays] = useState<number>(30);

  const { data, isLoading } = useQuery<BlogAnalytics>({
    queryKey: ['admin', 'blog_analytics', days],
    queryFn: () => adminAction('blog_analytics', { days }),
    staleTime: 60 * 1000,
  });

  if (isLoading) return <AdminPageSkeleton stats={3} rows={5} columns={3} />;

  const totalViews = data?.total_views ?? 0;
  const postsViewed = data?.posts_viewed ?? 0;
  const avgPerDay = data?.avg_views_per_day ?? 0;
  const chartData = (data?.views_by_day ?? []).map((d) => ({
    date: format(parseISO(d.date), 'd MMM'),
    views: d.views,
  }));
  const topPosts = data?.top_posts ?? [];

  return (
    <div className="space-y-10">
      <AdminPageHeader
        eyebrow="Analytics"
        title="Blog analytics"
        description="Pageviews across the resources blog. Track which posts are pulling weight and where to invest more writing."
        meta={`Last ${days} days`}
        actions={
          <AdminSegmented
            value={String(days)}
            onChange={(v) => setDays(Number(v))}
            options={RANGE_OPTS}
            size="sm"
          />
        }
      />

      <AdminStatRibbon
        items={[
          { label: 'Total views', value: totalViews.toLocaleString(), sub: `Last ${days} days` },
          { label: 'Posts viewed', value: postsViewed, sub: 'Distinct posts' },
          { label: 'Average / day', value: avgPerDay, sub: 'Across the window' },
        ]}
      />

      <AdminSection title="Views over time" description="Daily pageview cadence.">
        <AdminPanel padded>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(58,53,48,0.10)" />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(58,53,48,0.10)' }}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={10}
                  allowDecimals={false}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: '#faf7f1',
                    border: '1px solid rgba(58,53,48,0.14)',
                    borderRadius: 3,
                    fontSize: 11,
                  }}
                />
                <Line type="monotone" dataKey="views" stroke="#c2541a" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </AdminPanel>
      </AdminSection>

      <AdminSection title="Top posts" description="Ranked by total pageviews in the window.">
        {topPosts.length === 0 ? (
          <AdminEmptyState
            icon={FileText}
            title="No views yet"
            description="Once readers land on a post, they'll appear here ranked by traffic."
          />
        ) : (
          <AdminTable
            head={
              <>
                <th className={adminTableHeadCellClass}>Post</th>
                <th className={adminTableHeadCellClass} style={{ textAlign: 'right' }}>Views</th>
                <th className={adminTableHeadCellClass}></th>
              </>
            }
          >
            {topPosts.map(({ slug, post_id, title, views }, i) => (
              <tr
                key={slug}
                className={adminTableRowClass}
                style={{ borderBottom: i === topPosts.length - 1 ? 'none' : '1px solid rgba(58,53,48,0.06)' }}
              >
                <td className={adminTableCellClass}>
                  {post_id ? (
                    <Link to={`/admin/blog/edit/${post_id}`} className="text-foreground hover:text-[#c2541a] transition-colors">
                      {title}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">{title}</span>
                  )}
                  <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">/{slug}</div>
                </td>
                <td className={`${adminTableCellClass} text-right tabular-nums text-foreground`}>
                  {views.toLocaleString()}
                </td>
                <td className={`${adminTableCellClass} text-right`}>
                  <Button asChild size="sm" variant="ghost" className="text-[11px] uppercase tracking-[0.16em] h-7">
                    <a href={`/resources/${slug}`} target="_blank" rel="noreferrer">View live</a>
                  </Button>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}
      </AdminSection>
    </div>
  );
}
